import React, { useMemo } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import type {
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import type { FieldArrayRenderProps } from "react-final-form-arrays";
import type { FieldDefinition } from "./types";
import { ErrorMessage } from "../ErrorMessage";

const getItemStyle = (
  draggableStyle: React.CSSProperties
): React.CSSProperties => ({
  marginBottom: 8,
  ...draggableStyle,
});

export interface ArrayItemRowProps {
  name: string;
  index: number;
  item: any;
  fields: FieldArrayRenderProps<any, HTMLElement>["fields"];
  fieldName: string;
  subschema?: Record<string, FieldDefinition>;
  spacing: number;
  allValues: Record<string, unknown>;
  getField: (
    fieldName: string,
    field: FieldDefinition,
    values: Record<string, unknown>,
    allValues: Record<string, unknown>
  ) => ReactNode;
  removeText: string;
  restoreText: string;
  softDelete?: boolean;
  disableRemove: boolean;
  disableReorder: boolean;
  hideRemoveButton: boolean;
  touched?: boolean;
  error?: string;
  isLast?: boolean;
}

/**
 * Элемент массива в одну строку: драг | поля subschema | кнопка удаления.
 * Подключается через itemComponent в определении поля type: "array".
 */
export const ArrayItemRow: React.FC<ArrayItemRowProps> = ({
  name,
  index,
  item,
  fields,
  subschema,
  spacing,
  allValues,
  getField,
  removeText,
  restoreText,
  softDelete,
  disableRemove,
  disableReorder,
  hideRemoveButton,
  touched,
  error,
}) => {
  const { t } = useTranslation();

  const arrayItemFields = useMemo(() => {
    if (!subschema) return null;
    return Object.keys(subschema)
      .filter((subName) => {
        const field = subschema[subName];
        return field.isVisible
          ? field.isVisible({
              values: fields.value?.[index],
              allValues,
            })
          : true;
      })
      .map((subName) =>
        getField(
          `${name}.${subName}`,
          { ...subschema[subName] },
          fields.value?.[index],
          allValues
        )
      );
  }, [subschema, fields.value, index, allValues, getField, name]);

  let finalError = error;
  if (typeof error === "object" && error !== null && "0" in error) {
    finalError = Object.values(error).join("");
  }

  return (
    <Draggable
      key={name}
      draggableId={name}
      index={index}
      isDragDisabled={disableReorder}
    >
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <>
          {touched && typeof finalError === "string" && (
            <ErrorMessage error={{ message: finalError }} />
          )}
          <Paper
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(provided.draggableProps.style || {})}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              marginBottom: 1,
              opacity: item?.deleted ? 0.5 : 1,
              border: snapshot.isDragging ? "2px solid" : "none",
              borderColor: "primary.main",
            }}
          >
            {!disableReorder && (
              <Box
                {...provided.dragHandleProps}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "grab",
                  color: "action.active",
                }}
                title={t("shared.form.reorderHint", {
                  defaultValue: "Перетащите для изменения порядка",
                })}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Grid container spacing={spacing} sx={{ flexWrap: "nowrap" }}>
                {arrayItemFields}
              </Grid>
            </Box>
            {!hideRemoveButton &&
              (softDelete && item?.deleted ? (
                <Tooltip title={restoreText}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        fields.update(index, { ...item, deleted: false })
                      }
                      disabled={disableRemove}
                      aria-label={restoreText}
                    >
                      <SettingsBackupRestoreIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={removeText}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (softDelete && item?._id) {
                          fields.update(index, { ...item, deleted: true });
                        } else {
                          fields.remove(index);
                        }
                      }}
                      disabled={disableRemove}
                      aria-label={removeText}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ))}
          </Paper>
        </>
      )}
    </Draggable>
  );
};
